/**
 * Company profile command-palette search: field catalog, ranking, and focus events.
 */

import { currencyFormat, parseDelimitedList } from "../helpers.js";
import { parseJsonArray } from "./companyJobPostings.js";
import {
  GOVERNMENT_PROGRAMS_INTEREST_OPTIONS,
  ONBOARDING_PROGRESS_FIELD,
  loadOnboardingGrantInterestsFromCompanyFields,
  parseWorkingCapitalInterests,
} from "./interestedOpportunities.js";
import { parseOnboardingProgress } from "./onboardingSaveUtils.js";

export const COMPANY_PROFILE_SEARCH_FOCUS_EVENT = "company-profile-search:focus";
export const OPEN_COMPANY_PROFILE_SEARCH_EVENT = "company-profile-search:open";
export const COMPANY_PROFILE_CALL_FOCUS_EVENT = "company-profile-search:call-focus";
export const COMPANY_PROFILE_ONBOARDING_FOCUS_EVENT = "company-profile-search:onboarding-focus";

export const companyProfileSearchShortcutLabel =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform)
    ? "⌘⇧K"
    : "Ctrl+Shift+K";

let pendingCompanyProfileSearchFocus = null;
let pendingCompanyProfileFieldFocus = null;
let pendingCompanyProfileCallFocus = null;
let pendingCompanyProfileOnboardingFocus = null;

/** Open the shared company profile search palette (Layout owns the instance). */
export function openCompanyProfileSearch(detail = {}) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(OPEN_COMPANY_PROFILE_SEARCH_EVENT, { detail })
  );
}

/** Dispatched when a palette result should open Grant Q&A with context. */
export function dispatchCompanyProfileSearchFocus(detail) {
  pendingCompanyProfileSearchFocus = detail || null;
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(COMPANY_PROFILE_SEARCH_FOCUS_EVENT, { detail })
  );
}

/** Read + clear a focus request that may have fired before Grant Q&A mounted. */
export function consumePendingCompanyProfileSearchFocus() {
  const detail = pendingCompanyProfileSearchFocus;
  pendingCompanyProfileSearchFocus = null;
  return detail;
}

/** Queue a field focus for after navigation / form mount. */
export function dispatchCompanyProfileFieldFocus(fieldName) {
  pendingCompanyProfileFieldFocus = fieldName || null;
}

export function consumePendingCompanyProfileFieldFocus() {
  const fieldName = pendingCompanyProfileFieldFocus;
  pendingCompanyProfileFieldFocus = null;
  return fieldName;
}

/** Queue / dispatch focus for a call recording card. */
export function dispatchCompanyProfileCallFocus(detail) {
  pendingCompanyProfileCallFocus = detail || null;
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(COMPANY_PROFILE_CALL_FOCUS_EVENT, { detail })
  );
}

export function consumePendingCompanyProfileCallFocus() {
  const detail = pendingCompanyProfileCallFocus;
  pendingCompanyProfileCallFocus = null;
  return detail;
}

/** Switch the Company Earnings Estimate tab, then scroll to that subsection. */
export function dispatchCompanyProfileOnboardingFocus(detail) {
  pendingCompanyProfileOnboardingFocus = detail || null;
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(COMPANY_PROFILE_ONBOARDING_FOCUS_EVENT, { detail })
  );
}

export function consumePendingCompanyProfileOnboardingFocus() {
  const detail = pendingCompanyProfileOnboardingFocus;
  pendingCompanyProfileOnboardingFocus = null;
  return detail;
}

export function focusCompanyProfileCallRecording(recordingId, { openTranscript = false } = {}) {
  if (!recordingId || typeof document === "undefined") return;
  const tryFocus = () => {
    const el = document.getElementById(`call-recording-${recordingId}`);
    if (!el) return false;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    el.classList?.add?.("ring-2", "ring-gray-900", "ring-offset-2");
    window.setTimeout(() => {
      el.classList?.remove?.("ring-2", "ring-gray-900", "ring-offset-2");
    }, 1800);
    if (openTranscript) {
      const toggle = el.querySelector('[data-call-transcript-toggle="true"]');
      const panel = el.querySelector("[data-call-transcript-panel]");
      if (toggle && !panel) {
        toggle.click();
      }
    }
    return true;
  };

  if (tryFocus()) return;
  window.setTimeout(tryFocus, 350);
  window.setTimeout(tryFocus, 800);
}

/** Build Profile & Company deep-link for a search result. */
export function buildCompanyProfileSearchUrl(company, item = {}) {
  const domain = company?.fields?.Domain;
  if (!domain) return "/company-info-section";
  const params = new URLSearchParams();
  params.set("website", domain);
  if (company?.fields?.Name) params.set("company", company.fields.Name);
  const panel = item.sectionId || "company-general";
  params.set("panel", panel);
  return `/company-info-section?${params.toString()}`;
}

/**
 * Canonical searchable fields on the company profile form.
 * Labels match CompanyProfileFields section copy.
 * `kind` tells the indexer how to read Airtable vs live form state.
 */
export const COMPANY_PROFILE_FIELD_CATALOG = [
  // Interests
  {
    fieldName: "interestedOpportunities",
    airtableField: "InterestedOpportunities",
    label: "Earnings improvement",
    sectionId: "company-interests",
    category: "Interests",
    kind: "workingCapital",
    keywords: ["interests", "working capital", "earnings improvement"],
  },
  {
    fieldName: "governmentProgramsInterest",
    airtableField: "GovernmentProgramsInterest",
    label: "Government programs",
    sectionId: "company-interests",
    category: "Interests",
    kind: "governmentPrograms",
    keywords: ["interests", "government programs"],
  },

  // General
  { fieldName: "Name", label: "Company Name", sectionId: "company-general", keywords: ["name", "company"] },
  { fieldName: "Domain", label: "Domain", sectionId: "company-general", keywords: ["website", "domain"] },
  { fieldName: "LinkedInUrl", label: "LinkedIn URL", sectionId: "company-general", keywords: ["linkedin"] },
  { fieldName: "Description", label: "Description", sectionId: "company-general", keywords: ["about", "summary"] },
  { fieldName: "Industry", label: "Industry", sectionId: "company-general" },
  { fieldName: "Employees", label: "Employees", sectionId: "company-general", keywords: ["headcount", "team size"] },
  { fieldName: "AnnualRevenue", label: "Annual Revenue", sectionId: "company-general", keywords: ["revenue"] },
  { fieldName: "OperationalCosts", label: "Operational Costs ($)", sectionId: "company-general", keywords: ["opex", "costs"] },
  { fieldName: "CompanyType", label: "Company Type", sectionId: "company-general", keywords: ["legal structure"] },
  { fieldName: "Profitable?", label: "Profitable?", sectionId: "company-general", keywords: ["profit"] },
  {
    fieldName: "IndigenousTeamMember",
    label: "Indigenous",
    sectionId: "company-general",
    kind: "boolean",
    keywords: ["indigenous"],
  },
  {
    fieldName: "WomenLed",
    label: "Women-led",
    sectionId: "company-general",
    kind: "boolean",
    keywords: ["women led", "women-led"],
  },
  { fieldName: "Country", label: "Country", sectionId: "company-general" },
  { fieldName: "ProvinceOrState", label: "Province/State", sectionId: "company-general", keywords: ["province", "state"] },
  { fieldName: "Address", label: "Address", sectionId: "company-general" },
  { fieldName: "LegalName", label: "Legal name", sectionId: "company-general", keywords: ["legal name"] },
  { fieldName: "BusinessNumber", label: "Business number", sectionId: "company-general", keywords: ["business number", "bn"] },
  { fieldName: "Founded", label: "Founded", sectionId: "company-general", keywords: ["year founded"] },
  { fieldName: "FiscalYearEnd", label: "Fiscal Year End", sectionId: "company-general" },
  { fieldName: "LogoUrl", label: "Logo URL", sectionId: "company-general", keywords: ["logo"] },
  { fieldName: "FollowerCount", label: "LinkedIn Follower Count", sectionId: "company-general", keywords: ["linkedin", "followers"] },

  // Key assets (rendered under General)
  { fieldName: "OrgChart", label: "Org Chart", sectionId: "company-general", keywords: ["key assets", "org chart"] },
  { fieldName: "HiringPlan", label: "Hiring Plan", sectionId: "company-general", keywords: ["key assets"] },
  { fieldName: "ProformaFinancialModel", label: "Proforma", sectionId: "company-general", keywords: ["key assets", "proforma"] },
  { fieldName: "GoogleDoc", label: "Google Doc", sectionId: "company-general", keywords: ["key assets"] },
  { fieldName: "GoogleDrive", label: "Google Drive", sectionId: "company-general", keywords: ["key assets"] },
  {
    fieldName: "StrategicPriorities",
    label: "Strategic Priorities",
    sectionId: "company-general",
    category: "Strategic Priorities",
    keywords: ["key assets", "priorities", "strategy"],
  },
  { fieldName: "Technologies", label: "Technologies", sectionId: "company-general", keywords: ["tech stack"] },
  {
    fieldName: "LongDescription",
    label: "Website Content",
    sectionId: "company-general",
    keywords: ["website content", "website copy", "long description", "scraped"],
  },
  {
    fieldName: "CompanyDeepResearch",
    label: "Company Deep Research",
    sectionId: "company-general",
    category: "Deep Research",
    keywords: ["deep research", "research report", "ai research"],
  },
  {
    fieldName: "InterestedIn",
    label: "Interested In (Grants)",
    sectionId: "company-grant-qa",
    keywords: ["interestedin", "grants", "roadmap"],
  },

  // Strategic priorities & expenses
  { fieldName: "PlannedHires", label: "Planned Full-Time Hires", sectionId: "company-details", keywords: ["hiring", "hires", "strategic priorities"] },
  { fieldName: "LikelyCustomers", label: "Likely Customers", sectionId: "company-details", keywords: ["customers", "icp"] },
  { fieldName: "TopExpenses", label: "Top Expenses Next 12 Months", sectionId: "company-details", keywords: ["expenses"] },
  { fieldName: "ExpensiveVendors", label: "Most Expensive Vendors", sectionId: "company-details", keywords: ["vendors"] },
  { fieldName: "GovernmentGrantsPursued", label: "Government Grants Pursued", sectionId: "company-details", keywords: ["grants pursued"] },
  { fieldName: "AdditionalInfo", label: "Additional Information", sectionId: "company-details" },

  // Metrics
  { fieldName: "AnnualRecurringRevenue", label: "Annual Recurring Revenue (ARR)", sectionId: "company-metrics", keywords: ["arr", "revenue"] },
  { fieldName: "MonthlyRecurringRevenue", label: "Monthly Recurring Revenue (MRR)", sectionId: "company-metrics", keywords: ["mrr", "revenue"] },
  { fieldName: "ConversionRate", label: "Conversion Rate (%)", sectionId: "company-metrics" },
  { fieldName: "AvgDealSize", label: "Average Deal Size ($)", sectionId: "company-metrics", keywords: ["deal size", "acv"] },
  { fieldName: "CustomerLifetimeValue", label: "Customer Lifetime Value (LTV)", sectionId: "company-metrics", keywords: ["ltv"] },
  { fieldName: "Debt", label: "Debt ($)", sectionId: "company-metrics" },
  { fieldName: "AnnualTraffic", label: "Annual Traffic", sectionId: "company-metrics" },
  { fieldName: "EmailListSize", label: "Email List Size", sectionId: "company-metrics" },
  { fieldName: "MarketingCosts", label: "Marketing Costs (Last 30 Days)", sectionId: "company-metrics" },
  { fieldName: "NewCustomersLast30Days", label: "New Customers (Last 30 Days)", sectionId: "company-metrics" },
  { fieldName: "QualifiedLeadsLast30Days", label: "Qualified Leads (Last 30 Days)", sectionId: "company-metrics" },
  { fieldName: "MeetingsBookedLast30Days", label: "Meetings Booked (Last 30 Days)", sectionId: "company-metrics" },
  { fieldName: "ProposalsSentLast30Days", label: "Proposals Sent (Last 30 Days)", sectionId: "company-metrics" },
  { fieldName: "DealsClosedLast30Days", label: "Deals Closed (Last 30 Days)", sectionId: "company-metrics" },
];

export function formatSearchValue(value) {
  if (value == null || value === "") return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.map(String).filter(Boolean).join(", ");
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function formatMoney(value) {
  if (value == null || value === "") return "";
  const n = Number(String(value).replace(/[^0-9.-]/g, ""));
  if (!Number.isFinite(n) || n === 0) return formatSearchValue(value);
  return currencyFormat(n);
}

function hasText(value) {
  return value != null && String(value).trim() !== "";
}

function slugId(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48);
}

function toList(value) {
  if (Array.isArray(value)) return value.map((item) => String(item || "").trim()).filter(Boolean);
  if (!hasText(value)) return [];
  return String(value)
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function humanizeLabel(value) {
  return String(value || "")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function plainText(value) {
  return String(value || "")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/[#>*_`~|]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function readCatalogRaw(entry, companyFields, formData) {
  const live =
    formData && Object.prototype.hasOwnProperty.call(formData, entry.fieldName)
      ? formData[entry.fieldName]
      : undefined;

  if (entry.kind === "workingCapital") {
    if (Array.isArray(live)) return live;
    return parseWorkingCapitalInterests(companyFields.InterestedOpportunities);
  }
  if (entry.kind === "governmentPrograms") {
    if (Array.isArray(live)) return live;
    return parseDelimitedList(
      companyFields.GovernmentProgramsInterest,
      GOVERNMENT_PROGRAMS_INTEREST_OPTIONS
    );
  }
  if (entry.kind === "boolean") {
    if (live !== undefined) return live === true;
    return companyFields[entry.fieldName] === true;
  }
  if (live !== undefined && live !== null && live !== "") return live;
  const key = entry.airtableField || entry.fieldName;
  return companyFields[key];
}

export function truncatePreview(text, max = 96) {
  const s = String(text || "").replace(/\s+/g, " ").trim();
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

/**
 * Score a result against a query. Higher is better. 0 = no match.
 * Supports multi-word keyword queries (all words must appear somewhere).
 */
export function scoreSearchMatch(query, { title, subtitle, keywords = [], value = "" }) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return 1;

  const titleL = String(title || "").toLowerCase();
  const subtitleL = String(subtitle || "").toLowerCase();
  const valueL = String(value || "").toLowerCase();
  const kw = keywords.map((k) => String(k).toLowerCase());
  const hay = `${titleL} ${subtitleL} ${valueL} ${kw.join(" ")}`;

  if (titleL === q) return 100;
  if (titleL.startsWith(q)) return 90;
  if (titleL.includes(q)) return 75;
  if (kw.some((k) => k === q || k.startsWith(q))) return 70;
  if (kw.some((k) => k.includes(q))) return 55;
  if (subtitleL.includes(q)) return 45;
  if (valueL.includes(q)) return 35;

  const words = q.split(/\s+/).filter((w) => w.length > 1);
  if (words.length > 1 && words.every((w) => hay.includes(w))) {
    return 40;
  }

  return 0;
}

export function buildSectionSearchItems(sectionNavItems = []) {
  return sectionNavItems.map((item) => ({
    id: `section-${item.id}`,
    resultKey: `section-${item.id}`,
    type: "section",
    sectionId: item.id,
    title: item.label,
    subtitle: "Jump to section",
    keywords: [item.label, item.id.replace(/^company-/, "").replace(/-/g, " ")],
    category: "Sections",
  }));
}

export function buildFieldSearchItems(company, formData = null) {
  const fields = company?.fields || {};

  return COMPANY_PROFILE_FIELD_CATALOG.map((entry) => {
    const raw = readCatalogRaw(entry, fields, formData);
    const value = formatSearchValue(raw);
    const hasValue = Boolean(value && value !== "n/a");
    return {
      id: `field-${entry.fieldName}`,
      resultKey: `field-${entry.fieldName}`,
      type: "field",
      fieldName: entry.fieldName,
      focusId: entry.fieldName,
      sectionId: entry.sectionId,
      title: entry.label,
      subtitle: hasValue ? truncatePreview(value) : "Empty — jump to field",
      value,
      keywords: entry.keywords || [],
      category: entry.category || "Fields",
      empty: !hasValue,
    };
  });
}

function contentItem({
  id,
  title,
  subtitle,
  value,
  keywords = [],
  category,
  sectionId,
  focusId,
  fieldName,
  onboardingTab,
}) {
  return {
    id,
    resultKey: id,
    type: "content",
    title,
    subtitle,
    matchSubtitle: "",
    value: formatSearchValue(value),
    keywords,
    category,
    sectionId,
    focusId: focusId || fieldName || sectionId,
    fieldName: fieldName || null,
    onboardingTab: onboardingTab || null,
  };
}

const LONG_FORM_FIELDS = [
  {
    fieldName: "CompanyDeepResearch",
    label: "Company Deep Research",
    sectionId: "company-general",
    category: "Deep Research",
  },
  {
    fieldName: "LongDescription",
    label: "Website Content",
    sectionId: "company-general",
    category: "Fields",
  },
  {
    fieldName: "StrategicPriorities",
    label: "Strategic Priorities",
    sectionId: "company-general",
    category: "Strategic Priorities",
  },
  {
    fieldName: "Description",
    label: "Description",
    sectionId: "company-general",
    category: "Fields",
  },
];

/** Split a long profile field into passages so a phrase inside the body can rank on its own. */
function buildLongFormChunks(company, formData) {
  const fields = company?.fields || {};
  const items = [];

  LONG_FORM_FIELDS.forEach((entry) => {
    const live = formData?.[entry.fieldName];
    const raw = hasText(live) ? live : fields[entry.fieldName];
    const text = String(raw || "").trim();
    if (text.length < 280) return;

    const sections = [];
    let current = { title: "", lines: [] };
    text.split(/\n/).forEach((line) => {
      const heading = line.match(/^#{1,3}\s+(.+)/);
      if (heading) {
        if (current.lines.join("").trim()) sections.push(current);
        current = { title: heading[1].trim(), lines: [] };
      } else {
        current.lines.push(line);
      }
    });
    if (current.lines.join("").trim()) sections.push(current);

    const passages =
      sections.length > 1
        ? sections
        : text
            .split(/\n{2,}/)
            .map((block) => ({ title: "", lines: [block] }))
            .filter((block) => plainText(block.lines.join(" ")).length > 40);

    passages.slice(0, 16).forEach((passage, index) => {
      const body = plainText(passage.lines.join(" "));
      if (body.length < 24) return;
      const title = passage.title || truncatePreview(body, 56);
      items.push(
        contentItem({
          id: `passage-${entry.fieldName}-${index}-${slugId(title)}`,
          title,
          subtitle: entry.label,
          value: body,
          keywords: [],
          category: entry.category,
          sectionId: entry.sectionId,
          fieldName: entry.fieldName,
          focusId: entry.fieldName,
        })
      );
    });
  });

  return items;
}

function buildInterestValueItems(company, formData) {
  const fields = company?.fields || {};
  const items = [];

  const earnings = readCatalogRaw(
    { fieldName: "interestedOpportunities", kind: "workingCapital" },
    fields,
    formData
  );
  toList(earnings).forEach((label) => {
    items.push(
      contentItem({
        id: `interest-earnings-${slugId(label)}`,
        title: label,
        subtitle: "Interests · Earnings improvement",
        value: label,
        keywords: ["interests", "earnings improvement"],
        category: "Interests",
        sectionId: "company-interests",
        focusId: "interestedOpportunities",
        fieldName: "interestedOpportunities",
      })
    );
  });

  const programs = readCatalogRaw(
    { fieldName: "governmentProgramsInterest", kind: "governmentPrograms" },
    fields,
    formData
  );
  toList(programs).forEach((label) => {
    items.push(
      contentItem({
        id: `interest-programs-${slugId(label)}`,
        title: label,
        subtitle: "Interests · Government programs",
        value: label,
        keywords: ["interests", "government programs"],
        category: "Interests",
        sectionId: "company-interests",
        focusId: "governmentProgramsInterest",
        fieldName: "governmentProgramsInterest",
      })
    );
  });

  return items;
}

function pushFact(items, { tab, tabLabel, label, value }) {
  if (!hasText(value)) return;
  const formatted = formatSearchValue(value);
  if (!formatted || formatted === "n/a") return;
  items.push(
    contentItem({
      id: `earnings-${tab}-${slugId(label)}`,
      title: label,
      subtitle: `Company Earnings Estimate · ${tabLabel}`,
      value: formatted,
      keywords: [tabLabel],
      category: "Company Earnings Estimate",
      sectionId: "company-onboarding",
      focusId: `company-onboarding-${tab}`,
      onboardingTab: tab,
    })
  );
}

function aggregateNamedExpenses(items) {
  const aggregated = new Map();
  (Array.isArray(items) ? items : []).forEach((item) => {
    const name = String(item?.name || item?.toolName || item?.courseName || "").trim();
    if (!name) return;
    const amount =
      Number(String(item?.amount ?? item?.expense ?? "").replace(/[^0-9.-]/g, "")) || 0;
    const existing = aggregated.get(name.toLowerCase()) || { name, amount: 0 };
    existing.amount += amount;
    aggregated.set(name.toLowerCase(), existing);
  });
  return [...aggregated.values()];
}

function prefixedExpenses(data, prefix) {
  return Object.entries(data || {})
    .filter(([key, value]) => key.startsWith(`${prefix}_`) && key.endsWith("_expense") && hasText(value))
    .map(([key, amount]) => ({
      label: humanizeLabel(key.slice(prefix.length + 1, -"_expense".length)),
      amount,
    }));
}

function buildEarningsEstimateItems(company) {
  const raw = company?.fields?.[ONBOARDING_PROGRESS_FIELD];
  const overview = {
    id: "onboarding-snapshot",
    resultKey: "onboarding-snapshot",
    type: "content",
    sectionId: "company-onboarding",
    focusId: "company-onboarding",
    title: "Company Earnings Estimate",
    subtitle: "Jump to company earnings estimate",
    keywords: ["company earnings estimate", "earnings estimate", "onboarding"],
    category: "Company Earnings Estimate",
  };

  const progress = parseOnboardingProgress(raw);
  if (!progress) {
    return [{ ...overview, subtitle: "No company earnings estimate saved yet" }];
  }

  const data = progress.formData || {};
  const grantPayload = loadOnboardingGrantInterestsFromCompanyFields(company?.fields || {});
  const items = [
    {
      ...overview,
      subtitle: truncatePreview(
        [
          data.jobTitle,
          toList(data.industry).join(", "),
          data.employees != null ? `${data.employees} employees` : null,
        ]
          .filter(Boolean)
          .join(" · ") || "Saved company earnings estimate"
      ),
      value: [
        data.jobTitle,
        data.prospectEmail,
        toList(data.industry).join(", "),
        data.employees,
        data.revenue,
        data.expenses,
      ]
        .filter(hasText)
        .join(" "),
    },
  ];

  const location = [...toList(data.city), ...toList(data.state), ...toList(data.country)].join(", ");
  [
    ["Role", data.jobTitle],
    ["Contact email", data.prospectEmail],
    ["Employees", data.employees],
    ["Founded", data.founded],
    ["Legal structure", data.legalStructure],
    ["Location", location],
    ["Annual revenue", formatMoney(data.revenue)],
    ["Annual expenses", formatMoney(data.expenses)],
    ["Industries", toList(data.industry).join(", ")],
  ].forEach(([label, value]) => pushFact(items, { tab: "company", tabLabel: "Company", label, value }));

  pushFact(items, {
    tab: "software",
    tabLabel: "Software",
    label: "Monthly software spend",
    value: formatMoney(data.monthlySpend),
  });
  aggregateNamedExpenses(data.softwareExpenses).forEach((tool) => {
    pushFact(items, {
      tab: "software",
      tabLabel: "Software",
      label: tool.name,
      value: tool.amount ? formatMoney(tool.amount) : tool.name,
    });
  });

  toList(grantPayload.interests).forEach((label) => {
    pushFact(items, { tab: "grants", tabLabel: "Grants", label, value: label });
  });
  Object.entries(grantPayload.expenses || {}).forEach(([label, amount]) => {
    pushFact(items, {
      tab: "grants",
      tabLabel: "Grants",
      label,
      value: formatMoney(amount) || formatSearchValue(amount),
    });
  });
  [
    ["R&D last year", formatMoney(data.rdLastYear)],
    ["R&D this year", formatMoney(data.rdThisYear)],
    ["Other interest", grantPayload.otherLabel],
    ["Notes", data.governmentGrantsPursuedNotes],
    ["Other qualifiers", toList(grantPayload.otherTags).join(", ")],
    ["Programs agreed to pursue", toList(data.agreedToGrants).join(", ")],
    [
      "Excluded programs",
      toList(data.excludedGrantPrograms).map(humanizeLabel).join(", "),
    ],
  ].forEach(([label, value]) => pushFact(items, { tab: "grants", tabLabel: "Grants", label, value }));

  toList(data.clientOfferCategories).forEach((label) => {
    pushFact(items, { tab: "client-offer", tabLabel: "Client offers", label, value: label });
  });
  Object.entries(data.clientOfferExpenses || {}).forEach(([label, amount]) => {
    pushFact(items, {
      tab: "client-offer",
      tabLabel: "Client offers",
      label: humanizeLabel(label),
      value: formatMoney(amount) || formatSearchValue(amount),
    });
  });
  pushFact(items, {
    tab: "client-offer",
    tabLabel: "Client offers",
    label: "Other client offer",
    value: data.clientOfferOtherLabel,
  });

  [
    ["Planned hires", data.plannedHires],
    ["Current students", data.currentStudents],
    ["Potential student hires", data.potentialStudentHires],
    ["Contractor support", toList(data.hiringContractors).join(", ")],
  ].forEach(([label, value]) => pushFact(items, { tab: "hiring", tabLabel: "Hiring", label, value }));
  (Array.isArray(data.hiringRoles) ? data.hiringRoles : [])
    .filter((role) => role?.role)
    .forEach((role) => {
      const label = `${role.role}${role.quarter ? ` · ${role.quarter}` : ""}`;
      pushFact(items, { tab: "hiring", tabLabel: "Hiring", label, value: label });
    });
  prefixedExpenses(data, "hiringContractor").forEach((row) => {
    pushFact(items, {
      tab: "hiring",
      tabLabel: "Hiring",
      label: row.label,
      value: formatMoney(row.amount),
    });
  });

  const yesNo = (value) => (value === true || value === "true" ? "Yes" : value === false || value === "false" ? "No" : "");
  [
    ["Business outside Canada", yesNo(data.businessOutsideCanada)],
    ["Outside-Canada investment", formatMoney(data.businessOutsideCanada_expense)],
    ["Launching a new product or service", yesNo(data.newProductService)],
    ["New product or service investment", formatMoney(data.newProductService_expense)],
  ].forEach(([label, value]) => pushFact(items, { tab: "growth", tabLabel: "Growth", label, value }));

  pushFact(items, {
    tab: "ai",
    tabLabel: "AI & automation",
    label: "Monthly AI spend",
    value: formatMoney(data.aiSpend),
  });
  toList(data.aiGoals).forEach((label) => {
    pushFact(items, { tab: "ai", tabLabel: "AI & automation", label: `AI goal: ${label}`, value: label });
  });
  toList(data.automationAreas).forEach((label) => {
    pushFact(items, {
      tab: "ai",
      tabLabel: "AI & automation",
      label: `Automation: ${label}`,
      value: label,
    });
  });
  aggregateNamedExpenses(data.aiExpenses).forEach((tool) => {
    pushFact(items, {
      tab: "ai",
      tabLabel: "AI & automation",
      label: tool.name,
      value: tool.amount ? formatMoney(tool.amount) : tool.name,
    });
  });
  prefixedExpenses(data, "automation").forEach((row) => {
    pushFact(items, {
      tab: "ai",
      tabLabel: "AI & automation",
      label: row.label,
      value: formatMoney(row.amount),
    });
  });

  const courseNames = (Array.isArray(data.trainingCourses) ? data.trainingCourses : [])
    .map((course) =>
      typeof course === "object" ? course?.name || course?.courseName : String(course || "").trim()
    )
    .filter(Boolean);
  courseNames.forEach((name) => {
    pushFact(items, { tab: "expenses", tabLabel: "Expenses", label: name, value: name });
  });
  aggregateNamedExpenses(data.trainingCourses).forEach((course) => {
    if (!course.amount) return;
    pushFact(items, {
      tab: "expenses",
      tabLabel: "Expenses",
      label: `${course.name} cost`,
      value: formatMoney(course.amount),
    });
  });
  aggregateNamedExpenses(data.otherExpenses).forEach((expense) => {
    pushFact(items, {
      tab: "expenses",
      tabLabel: "Expenses",
      label: expense.name,
      value: expense.amount ? formatMoney(expense.amount) : expense.name,
    });
  });

  [data.teamEmail1, data.teamEmail2, data.teamEmail3].filter(hasText).forEach((email) => {
    pushFact(items, { tab: "team", tabLabel: "Team", label: email, value: email });
  });

  return items;
}

function buildJobPostingItems(company) {
  const fields = company?.fields || {};
  const openings = parseJsonArray(fields.JobPostings);
  const sources = parseJsonArray(fields.JobPostingSources);
  const items = [];

  openings.forEach((job, index) => {
    const title = String(job?.title || "").trim();
    if (!title) return;
    const bits = [job.location, job.salary ? formatMoney(job.salary) : ""].filter(hasText);
    items.push(
      contentItem({
        id: `job-${job.id || index}-${slugId(title)}`,
        title,
        subtitle: bits.join(" · ") || "Open Job Postings",
        value: [title, job.location, job.salary, job.url].filter(hasText).join(" "),
        keywords: ["job", "opening", "hiring", job.isRd ? "r&d" : ""].filter(Boolean),
        category: "Open Job Postings",
        sectionId: "company-job-postings",
        focusId: "company-job-postings",
      })
    );
  });

  sources.forEach((source, index) => {
    const label = String(source?.label || source?.url || "").trim();
    if (!label) return;
    items.push(
      contentItem({
        id: `job-source-${source.id || index}-${slugId(label)}`,
        title: label,
        subtitle: "Job posting source",
        value: [label, source.kind, source.url].filter(hasText).join(" "),
        keywords: ["job source", "careers", source.kind].filter(Boolean),
        category: "Open Job Postings",
        sectionId: "company-job-postings",
        focusId: "company-job-postings",
      })
    );
  });

  return items;
}

/** Everything on the profile that is not a single catalog field: passages, interests, earnings, jobs. */
export function buildProfileContentSearchItems(company, formData = null) {
  return [
    ...buildInterestValueItems(company, formData),
    ...buildEarningsEstimateItems(company),
    ...buildLongFormChunks(company, formData),
    ...buildJobPostingItems(company),
  ];
}

/** @deprecated Use buildProfileContentSearchItems. Kept for callers that only want the earnings snapshot. */
export function buildOnboardingSearchItems(company) {
  return buildEarningsEstimateItems(company).filter((item) => item.id === "onboarding-snapshot");
}

export function filterAndRankSearchItems(items, query, { limit = 40 } = {}) {
  const q = String(query || "").trim();
  const scored = items
    .map((item) => ({
      item,
      score: scoreSearchMatch(q, {
        title: item.title,
        subtitle: item.matchSubtitle !== undefined ? item.matchSubtitle : item.subtitle,
        keywords: item.keywords,
        value: item.value,
      }),
    }))
    .filter(({ score }) => (q ? score > 0 : true))
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return String(a.item.title).localeCompare(String(b.item.title));
    })
    .slice(0, limit)
    .map(({ item, score }) => ({ ...item, score }));

  return scored;
}

export function groupSearchResults(items) {
  const order = [
    "Sections",
    "Interests",
    "Company Earnings Estimate",
    "Strategic Priorities",
    "Deep Research",
    "Fields",
    "Open Job Postings",
    "Grants",
    "Questions & Answers",
    "Call Recordings",
    "Google Doc",
    "Google Folders",
  ];
  const groups = {};
  for (const item of items) {
    const cat = item.category || "Other";
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(item);
  }
  return order
    .filter((cat) => groups[cat]?.length)
    .map((cat) => [cat, groups[cat]]);
}

export function focusCompanyProfileField(fieldName) {
  if (!fieldName || typeof document === "undefined") return;
  const tryFocus = () => {
    const el =
      document.getElementById(fieldName) ||
      document.querySelector(`[name="${CSS.escape(fieldName)}"]`);
    if (!el) return false;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    if (typeof el.focus === "function") {
      try {
        el.focus({ preventScroll: true });
      } catch {
        el.focus();
      }
    }
    el.classList?.add?.("ring-2", "ring-gray-900", "ring-offset-2");
    window.setTimeout(() => {
      el.classList?.remove?.("ring-2", "ring-gray-900", "ring-offset-2");
    }, 1600);
    return true;
  };

  if (tryFocus()) return;
  window.setTimeout(tryFocus, 350);
  window.setTimeout(tryFocus, 700);
}
